import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, Landmark } from "lucide-react";
import api from "@/api";
import { formatDate } from "@/constants";

const SOURCE_BADGE = {
  "service-public": "bg-blue-50 text-blue-800 border-blue-200",
  education: "bg-emerald-50 text-emerald-800 border-emerald-200",
  eduscol: "bg-teal-50 text-teal-800 border-teal-200",
  caf: "bg-sky-50 text-sky-800 border-sky-200",
  "3018": "bg-rose-50 text-rose-800 border-rose-200",
  jpe: "bg-violet-50 text-violet-800 border-violet-200",
};

export default function FamilyNewsBanner() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    api.get("/family-news").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (items.length < 2 || paused) return undefined;
    const t = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % items.length);
        setVisible(true);
      }, 420);
    }, 8000);
    return () => clearInterval(t);
  }, [items.length, paused]);

  if (items.length === 0) return null;
  const item = items[idx % items.length];
  const go = (n) => {
    setVisible(false);
    setTimeout(() => {
      setIdx((i) => (i + n + items.length) % items.length);
      setVisible(true);
    }, 320);
  };

  return (
    <section className="relative z-[1] py-10 sm:py-12 bg-slate-50/70" data-testid="family-news-section"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4 mb-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-1.5">En direct des institutions</p>
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900" data-testid="family-news-title">Actualités des familles</h2>
          </div>
          {items.length > 1 && (
            <div className="flex gap-1.5 shrink-0">
              <button type="button" onClick={() => go(-1)} aria-label="Actualité précédente" data-testid="family-news-prev-btn"
                className="h-8 w-8 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:text-emerald-700 hover:border-emerald-300 transition-colors">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => go(1)} aria-label="Actualité suivante" data-testid="family-news-next-btn"
                className="h-8 w-8 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:text-emerald-700 hover:border-emerald-300 transition-colors">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        <article
          key={item.id}
          className={`bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden grid sm:grid-cols-[220px_1fr] transition-all duration-500 ease-in-out ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2"}`}
          data-testid="family-news-card"
        >
          {item.image ? (
            <img src={item.image} alt="" loading="lazy" referrerPolicy="no-referrer"
              className="hidden sm:block h-full w-full object-cover max-h-44" data-testid="family-news-image" />
          ) : (
            <div className="hidden sm:flex items-center justify-center bg-gradient-to-br from-emerald-50 to-sky-50 min-h-[120px]" data-testid="family-news-image-fallback">
              <Landmark className="h-9 w-9 text-emerald-300" />
            </div>
          )}
          <div className="p-5 sm:p-6 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${SOURCE_BADGE[item.source] || "bg-slate-50 text-slate-700 border-slate-200"}`}
                data-testid="family-news-source-badge">
                {item.source_label}
              </span>
              {item.date && (
                <span className="text-xs text-slate-400" data-testid="family-news-date">{formatDate(item.date)}</span>
              )}
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 leading-snug" data-testid="family-news-item-title">
              {item.title}
            </h3>
            {item.summary && (
              <p className="text-sm text-slate-600 leading-relaxed mt-1.5 line-clamp-2" data-testid="family-news-summary">
                {item.summary}
              </p>
            )}
            <a href={item.link} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-800 hover:underline mt-2.5"
              data-testid="family-news-readmore">
              Lire la suite <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </article>

        {items.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-4" data-testid="family-news-dots">
            {items.slice(0, 10).map((it, i) => (
              <button key={it.id || i} type="button" onClick={() => go(i - idx)} aria-label={`Actualité ${i + 1}`}
                data-testid={`family-news-dot-${i}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === idx % items.length ? "w-5 bg-emerald-500" : "w-1.5 bg-slate-300 hover:bg-slate-400"}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
